import { AlertTriangle } from "lucide-react";

import type { IEndCampaignTarget } from "@/@types/campaign";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEndCampaign } from "../hooks/use-end-campaign";
import { formatDateToBR } from "../utils/date-utils";
import { toDayKey } from "../utils/end-campaign";
import { END_CAMPAIGN_LABELS } from "../utils/messages";
import { CampaignConflictModal } from "./campaign-conflict-modal";

const END_DATE_FIELD_ID: string = "endCampaignDate";

export function EndCampaignModal(props: IEndCampaignTarget): JSX.Element {
  const { form, isLoading, conflicts, submit, close, clearConflicts } =
    useEndCampaign(props);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(submit)}
        className="flex flex-col gap-4"
        noValidate
      >
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{END_CAMPAIGN_LABELS.NOTICE}</AlertDescription>
        </Alert>

        <FormField
          control={form.control}
          name="endDate"
          render={({ field }) => (
            <FormItem className="space-y-1">
              <Label htmlFor={END_DATE_FIELD_ID} className="text-sm">
                {END_CAMPAIGN_LABELS.DATE_FIELD}
              </Label>
              <FormControl>
                <Input
                  {...field}
                  id={END_DATE_FIELD_ID}
                  type="date"
                  disabled={isLoading}
                />
              </FormControl>
              <p className="text-xs text-muted-foreground">
                {END_CAMPAIGN_LABELS.CURRENT_END}{" "}
                {formatDateToBR(toDayKey(props.currentEndDate))}
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={close}
            disabled={isLoading}
          >
            {END_CAMPAIGN_LABELS.CANCEL}
          </Button>
          <Button type="submit" variant="destructive" disabled={isLoading}>
            {END_CAMPAIGN_LABELS.ACTION}
          </Button>
        </div>
      </form>

      <CampaignConflictModal
        isOpen={conflicts.length > 0}
        conflicts={conflicts}
        onClose={clearConflicts}
        onGoBack={clearConflicts}
      />
    </Form>
  );
}
